import React, { useState, useEffect } from 'react';
import { Box, IconButton, Typography, Tooltip, Chip } from '@mui/material';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import { collection, doc, getDoc, setDoc, updateDoc, onSnapshot } from 'firebase/firestore';
import { getFirestore } from 'firebase/firestore';
import { firebaseConfig } from '../firebaseConfig';
import { initializeApp } from 'firebase/app';
import { getAuth, onAuthStateChanged } from 'firebase/auth';

// Initialize Firebase
const app = initializeApp(firebaseConfig);
const db = getFirestore(app);
const auth = getAuth(app);

interface VoteData {
  upvotes: { [userId: string]: { timestamp: Date; userName: string } };
  downvotes: { [userId: string]: { timestamp: Date; userName: string } };
}

// Function to get user name from users collection
const getUserNameFromFirestore = async (userId: string): Promise<string> => {
  try {
    const userDoc = await getDoc(doc(db, 'users', userId));
    if (userDoc.exists()) {
      const userData = userDoc.data();
      // Return name if it exists and is not empty, otherwise return email
      if (userData.name && userData.name.trim() !== '') {
        return userData.name;
      } else if (userData.email) {
        return userData.email;
      }
    }
    // Fallback to email from auth if user doc doesn't exist
    return 'Anonymous';
  } catch (error) {
    console.error('Error fetching user name:', error);
    return 'Anonymous';
  }
};

interface VotingSystemProps {
  collectionName: string;
  documentId: string;
  size?: 'small' | 'medium' | 'large';
  showCounts?: boolean;
  showTooltips?: boolean;
}

const VotingSystem: React.FC<VotingSystemProps> = ({ 
  collectionName, 
  documentId, 
  size = 'medium',
  showCounts = true,
  showTooltips = true
}) => {
  const [voteData, setVoteData] = useState<VoteData>({ upvotes: {}, downvotes: {} });
  const [currentUser, setCurrentUser] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  // Listen for auth state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user);
    });
    return () => unsubscribe();
  }, []);

  // Listen for vote data changes
  useEffect(() => {
    if (!documentId) return;

    const voteDocRef = doc(db, collectionName, documentId, 'votes', 'main');
    
    const unsubscribe = onSnapshot(voteDocRef, (doc) => {
      if (doc.exists()) {
        const data = doc.data() as VoteData;
        setVoteData(data);
      } else {
        // Initialize empty vote data
        setVoteData({ upvotes: {}, downvotes: {} });
      }
      setLoading(false);
    }, (error) => {
      console.error('Error listening to vote data:', error);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [collectionName, documentId]);

  const handleVote = async (voteType: 'upvote' | 'downvote', event?: React.MouseEvent) => {
    // Prevent event bubbling to parent elements
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }
    
    if (!currentUser || !documentId) {
      console.log('User not authenticated or document ID missing');
      return;
    }

    const voteDocRef = doc(db, collectionName, documentId, 'votes', 'main');
    const currentVoteData = { ...voteData };
    
    const userId = currentUser.uid;
    const timestamp = new Date();
    
    // Get user name from Firestore
    const userName = await getUserNameFromFirestore(userId);

    // Check if user is clicking the same vote type they already have
    const currentUserVote = getUserVote();
    if (currentUserVote === voteType) {
      // Remove the vote by deleting it from the current vote type
      if (voteType === 'upvote') {
        delete currentVoteData.upvotes[userId];
      } else {
        delete currentVoteData.downvotes[userId];
      }
    } else {
      // Remove existing vote from the opposite category and add new vote
      if (voteType === 'upvote') {
        delete currentVoteData.downvotes[userId];
        currentVoteData.upvotes[userId] = { timestamp, userName };
      } else {
        delete currentVoteData.upvotes[userId];
        currentVoteData.downvotes[userId] = { timestamp, userName };
      }
    }

    try {
      await setDoc(voteDocRef, currentVoteData, { merge: true });
    } catch (error) {
      console.error('Error updating vote:', error);
    }
  };

  const getUserVote = () => {
    if (!currentUser) return null;
    const userId = currentUser.uid;
    if (voteData.upvotes[userId]) return 'upvote';
    if (voteData.downvotes[userId]) return 'downvote';
    return null;
  };

  const getUpvoteCount = () => Object.keys(voteData.upvotes || {}).length;
  const getDownvoteCount = () => Object.keys(voteData.downvotes || {}).length;

  const [voterNames, setVoterNames] = useState<{ [userId: string]: string }>({});

  // Function to fetch and cache voter names
  const fetchVoterNames = async (votes: { [userId: string]: { timestamp: Date; userName: string } }) => {
    const names: { [userId: string]: string } = {};
    const userIds = Object.keys(votes);
    
    for (const userId of userIds) {
      if (!voterNames[userId]) {
        names[userId] = await getUserNameFromFirestore(userId);
      } else {
        names[userId] = voterNames[userId];
      }
    }
    
    if (Object.keys(names).length > 0) {
      setVoterNames(prev => ({ ...prev, ...names }));
    }
  };

  // Fetch voter names when vote data changes
  useEffect(() => {
    if (voteData.upvotes || voteData.downvotes) {
      const allVotes = { ...voteData.upvotes, ...voteData.downvotes };
      fetchVoterNames(allVotes);
    }
  }, [voteData.upvotes, voteData.downvotes]);

  const getVoterNames = (votes: { [userId: string]: { timestamp: Date; userName: string } }) => {
    const names = Object.keys(votes).map(userId => voterNames[userId] || 'Loading...');
    const filteredNames = names.filter(name => name !== 'Loading...');
    return filteredNames.length > 0 ? filteredNames.join(', ') : 'No votes yet';
  };

  if (loading) {
    return <Box sx={{ display: 'flex', gap: 1 }}><Typography variant="body2">Loading...</Typography></Box>;
  }

  const userVote = getUserVote();
  const upvoteCount = getUpvoteCount();
  const downvoteCount = getDownvoteCount();

  const iconSize = size === 'small' ? 16 : size === 'large' ? 24 : 20;

  return (
    <Box 
      sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
      onClick={(e) => e.stopPropagation()}
    >
      <Tooltip 
        title={showTooltips ? `Upvoted by: ${getVoterNames(voteData.upvotes)}` : ''}
        disableHoverListener={!showTooltips}
      >
        <IconButton
          size={size}
          onClick={(e) => handleVote('upvote', e)}
          sx={{
            color: userVote === 'upvote' ? 'success.main' : 'text.secondary',
            '&:hover': {
              color: 'success.main',
              backgroundColor: 'success.light',
            }
          }}
        >
          <ThumbUpIcon sx={{ fontSize: iconSize }} />
        </IconButton>
      </Tooltip>

      {showCounts && (
        <Typography variant="body2" sx={{ minWidth: 20, textAlign: 'center' }}>
          {upvoteCount}
        </Typography>
      )}

      <Tooltip 
        title={showTooltips ? `Downvoted by: ${getVoterNames(voteData.downvotes)}` : ''}
        disableHoverListener={!showTooltips}
      >
        <IconButton
          size={size}
          onClick={(e) => handleVote('downvote', e)}
          sx={{
            color: userVote === 'downvote' ? 'error.main' : 'text.secondary',
            '&:hover': {
              color: 'error.main',
              backgroundColor: 'error.light',
            }
          }}
        >
          <Typography sx={{ fontSize: iconSize, lineHeight: 1 }}>💩</Typography>
        </IconButton>
      </Tooltip>

      {showCounts && (
        <Typography variant="body2" sx={{ minWidth: 20, textAlign: 'center' }}>
          {downvoteCount}
        </Typography>
      )}
    </Box>
  );
};

export default VotingSystem; 